import supabase from '../supabase/supabase'

/**
 * Workflow Service
 * Handles all company workflow-related database operations
 */

/**
 * Get current user's tenant ID
 */
const getCurrentTenantId = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('No authenticated user')
  }

  const { data, error } = await supabase
    .from('tenant_members')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single()

  if (error) throw error
  
  return data.tenant_id
}

/**
 * Get current user ID
 */
const getCurrentUserId = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  return user?.id
}

/**
 * Get all workflows for current tenant
 * @param {Object} options - Options for filtering
 * @param {boolean} options.onlyMine - If true, only return workflows created by current user
 */
export const getAllWorkflows = async (options = {}) => {
  try {
    const tenantId = await getCurrentTenantId()
    const userId = await getCurrentUserId()

    let query = supabase
      .from('workflows')
      .select(`
        *,
        workflow_steps (
          id,
          step_name,
          step_order,
          status,
          duration_days,
          is_critical,
          assigned_to,
          notes,
          started_at,
          completed_at
        )
      `)
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)

    // NOTE: Workflows table doesn't have created_by column yet
    // For now, non-admins see all workflows in their tenant
    // TODO: Add created_by column to workflows table and Edge Function
    // if (options.onlyMine && userId) {
    //   query = query.eq('created_by', userId)
    // }

    const { data, error } = await query.order('created_at', { ascending: false })

    if (error) throw error

    // Calculate progress for each workflow
    const workflowsWithProgress = data.map(workflow => {
      const steps = workflow.workflow_steps || []
      const completedSteps = steps.filter(s => s.status === 'completed').length
      const totalSteps = steps.length
      const completionPercentage = totalSteps > 0 
        ? Math.round((completedSteps / totalSteps) * 100) 
        : 0

      return {
        ...workflow,
        progress: {
          completedSteps,
          totalSteps,
          completionPercentage
        }
      }
    })

    return workflowsWithProgress
  } catch (error) {
    console.error('Error fetching workflows:', error)
    throw error
  }
}

/**
 * Get workflow by ID with all related data
 */
export const getWorkflowById = async (workflowId) => {
  try {
    const { data, error } = await supabase
      .from('workflows')
      .select(`
        *,
        workflow_steps (
          id,
          step_name,
          step_order,
          status,
          duration_days,
          is_critical,
          assigned_to,
          notes,
          started_at,
          completed_at,
          created_at
        ),
        workflow_notes (
          id,
          note_title,
          note_content,
          created_by,
          created_at
        ),
        workflow_audit_log (
          id,
          action,
          details,
          performed_by,
          performed_at
        )
      `)
      .eq('id', workflowId)
      .is('deleted_at', null)
      .single()

    if (error) throw error

    // Calculate progress
    const steps = data.workflow_steps || []
    const completedSteps = steps.filter(s => s.status === 'completed').length
    const totalSteps = steps.length
    const completionPercentage = totalSteps > 0 
      ? Math.round((completedSteps / totalSteps) * 100) 
      : 0

    return {
      ...data,
      progress: {
        completedSteps,
        totalSteps,
        completionPercentage
      }
    }
  } catch (error) {
    console.error('Error fetching workflow by ID:', error)
    throw error
  }
}

/**
 * Create new workflow via Edge Function
 */
export const createWorkflow = async (workflowData) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'workflows',
        ...workflowData,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to create workflow');

    return data;
  } catch (error) {
    console.error('Error creating workflow:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to create workflow');
  }
}

/**
 * Update workflow via Edge Function
 */
export const updateWorkflow = async (workflowId, updates) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'workflows/update',
        workflow_id: workflowId,
        ...updates,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to update workflow');

    return data;
  } catch (error) {
    console.error('Error updating workflow:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to update workflow');
  }
}

/**
 * Update workflow step via Edge Function
 */
export const updateWorkflowStep = async (stepId, updates) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'workflow-steps/update',
        step_id: stepId,
        ...updates,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to update workflow step');

    return data;
  } catch (error) {
    console.error('Error updating workflow step:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to update workflow step');
  }
}

/**
 * Add workflow note via Edge Function
 */
export const addWorkflowNote = async (workflowId, noteData) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'workflow-notes',
        workflow_id: workflowId,
        title: noteData.title,
        content: noteData.content,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to add workflow note');

    return data;
  } catch (error) {
    console.error('Error adding workflow note:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to add workflow note');
  }
}

/**
 * Add audit log entry
 * Note: Audit logs are now automatically created by Edge Functions
 * This function is kept for backward compatibility but may not be needed
 */
export const addWorkflowAuditEntry = async (workflowId, action, details) => {
  console.log('Audit entry would be created:', { workflowId, action, details });
  // Audit logs are now handled by Edge Functions
  return { workflowId, action, details };
}

/**
 * Soft delete workflow via Edge Function
 */
export const deleteWorkflow = async (workflowId) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'workflows/delete',
        workflow_id: workflowId,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to delete workflow');

    return data;
  } catch (error) {
    console.error('Error deleting workflow:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to delete workflow');
  }
}

export default {
  getAllWorkflows,
  getWorkflowById,
  createWorkflow,
  updateWorkflow,
  updateWorkflowStep,
  addWorkflowNote,
  addWorkflowAuditEntry,
  deleteWorkflow
}